import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
    ExploreComponent,
    InspireComponent,
    NeedHelpComponent,
    RecycleComponent
} from "./components/CardComponent";
import ChooseDeviceBelow from "./components/ChooseDeviceBelow";
import PrinterDevicePanel from "./components/PrinterDevicePanel/PrinterDevicePanel";
import { useDispatch, useSelector } from "react-redux";
import { UserEnrolledDevicesServiceInit, UserEnrolledDevicesServiceResetState } from "../../store/UserEnrolledDevices/UserEnrolledDevicesSlice";
import { EnrollmentStatus } from "../../actions/Dashboard";
import SectionHeader from "./components/SectionHeader/SectionHeader";
import AddDevice from "./components/AddDevice/AddDevice";
import DeviceListSliderView from "./components/DeviceList/DeviceListSliderView";
import DeviceListStackedView from "./components/DeviceList/DeviceListStackedView";
import sparkleWandIcon from "../../assets/images/BrotherPlus/wand-magic-sparkles-icon.svg";
import "./MyDevices.css";
import {
    GetUserDeviceRegistrationServiceInit,
    GetUserDeviceRegistratoinServiceReset
} from "../../store/UserDeviceRegistration/UserDeviceRegistrationSlice";
import {
    getFeatureSwitchByKey,
    getLocalSessionData
} from "../../utils/Authentication/handleUserSession";
import { featureSwitchName, LOCALSESSIONDATAKEYS, VALID_DEEP_LINK_SOURCE_VALUES } from "../../constants/AppConstants";
import { DEEP_LINK_PARAMS } from "../../constants/Routes";
import ErrorPage from "../../components/shared/errorPage/ErrorPage";
import HeaderComponent from "../../components/shared/header/HeaderComponent/HeaderComponent";
import { useTranslation } from 'react-i18next';
import { getCountryCode } from "../../utils/Internationalization/handleAppInternationalization";
import { getEnrollmentFailureErrorMessage, isDesktopOrBmcClient, mergeDeviceLists, pushMultipleDataLayer, removeNativeAppStorageItems, updateDeviceInfoToLocalStorage } from "../../constants/AppMethod";
import { UserEnrollmentFailedDevicesServiceInit, UserEnrollmentFailedDevicesServiceReset } from "../../store/UserEnrollmentFailedDevices/UserEnrollmentFailedDevicesSlice";
import EcoproAlert from "../../components/shared/alert/alert";
import EnrollmentFailedDeviceIcon from "../../assets/images/alert-printer-icon.svg";
import WarningAlert from "../../assets/images/warning-alert.svg";
import { ResetEnrollmentJourneySerialNumber } from "../../actions/MyAccount";

const MyDevices = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["translation"]);
    const deviceInfoPanel = useRef(null);
    const IsBrotherPlusFeatureSwitchEnabled = getFeatureSwitchByKey(featureSwitchName.BROTHER_PLUS);
    const {
        userDeviceList_loading,
        userDeviceList_data,
        userDeviceList_error,
        userDeviceList_success
    } = useSelector(state => state.ROOTREDUCER_RTK.GetUserEnrolledDevicesService);
    const userDeviceRegistrationResponse = useSelector(state => state.ROOTREDUCER_RTK.GetUserDeviceRegistrationService);
    const userEnrollmentFailedDevicesResponse = useSelector(state => state.ROOTREDUCER_RTK.GetUserEnrollmentFailedDevicesService);
    const { enrollmentJourneySerialNumber } = useSelector(state => state.EnrollmentSerialNumber);


    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const deepLinkDeviceId = searchParams.get(DEEP_LINK_PARAMS.DEVICE_ID);
    const deepLinkSection = searchParams.get(DEEP_LINK_PARAMS.SECTION);
    const deepLinkSource = searchParams.get(DEEP_LINK_PARAMS.SOURCE);
    const isEasyBuyDeepLink = deepLinkSection === DEEP_LINK_PARAMS.SECTION_EASYBUY;
    const isInvalidDeepLinkSource = isEasyBuyDeepLink && (deepLinkSource === null || !VALID_DEEP_LINK_SOURCE_VALUES.includes(deepLinkSource.toLowerCase()));
    
    const [deviceData, setDeviceData] = useState([]);
    const [selectedDeviceInfo, setSelectedDeviceInfo] = useState(null);
    const [invalidDeepLinkDevice, setInvalidDeepLinkDevice] = useState(false);
    const countryCode = getCountryCode();
    const isNativeApp = isDesktopOrBmcClient();
    const [displayEnrollmentInProgressMessage, setDisplayEnrollmentInProgressMessage] = useState(false);
    const hasScrolledToEasyBuy = useRef(false);
    const handleEasyBuyScrolled = useCallback(() => {
        hasScrolledToEasyBuy.current = true;
    }, []);

    useEffect(() => {
        // reset both API to avoid duplicacy, because both API results are being merged later in code
        dispatch(UserEnrolledDevicesServiceResetState());
        dispatch(UserEnrollmentFailedDevicesServiceReset());

        // intiate APi calls
        dispatch(UserEnrolledDevicesServiceInit());
        dispatch(UserEnrollmentFailedDevicesServiceInit());
        removeNativeAppStorageItems();
    }, [dispatch]);
    const enrolledDevices =
        userDeviceList_data?.userEnrolledDeviceViewModel || [];

    const failedDevices =
        userEnrollmentFailedDevicesResponse?.data?.userEnrollmentFailedDeviceViewModel || [];
    const isEcoProFailed =
        selectedDeviceInfo?.services?.ecopro?.isSuccess === false;
    const isDeviceUsable =
        selectedDeviceInfo?.isEnrollmentSuccessful;

    useEffect(() => {
        if (
            userDeviceList_success &&
            userEnrollmentFailedDevicesResponse?.success
        ) {
            const mergedDevices = mergeDeviceLists(enrolledDevices, failedDevices);

            setDeviceData(mergedDevices);

            if (mergedDevices.length > 0) {

                let selected = null;

                // 1. Deep-link device selection (highest priority)
                if (deepLinkDeviceId) {
                    selected = mergedDevices.find(
                        d => d.deviceId === deepLinkDeviceId
                    );
                    if (!selected) {
                        setInvalidDeepLinkDevice(true);
                    }
                }

                // 2. Preserve previous selection
                if (!selected && selectedDeviceInfo?.deviceId) {
                    selected = mergedDevices.find(
                        d => d.deviceId === selectedDeviceInfo.deviceId
                    );
                }

                // 3. If not found, try native app stored device
                if (!selected && isNativeApp) {
                    const deviceId = getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID);

                    selected = mergedDevices.find(
                        d => d.deviceId === deviceId
                    );
                }

                // 4. Fallback to first device
                if (!selected) {
                    selected = mergedDevices[0];
                }
                if (
                    selected?.deviceId !== selectedDeviceInfo?.deviceId
                ) {
                    setSelectedDeviceInfo(selected);
                }
            }
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        userDeviceList_success,
        userEnrollmentFailedDevicesResponse.success,
        enrolledDevices,
        failedDevices,
        isNativeApp
    ]);


    useEffect(() => {
        if (selectedDeviceInfo !== null) {
            // reset GetUserDeviceRegistration API state of previous devices
            dispatch(GetUserDeviceRegistratoinServiceReset());

            const deviceKeysToSessionMap = {
                deviceId: LOCALSESSIONDATAKEYS.DEVICE_ID,
                model: LOCALSESSIONDATAKEYS.DEVICE_MODEL,
                serialNumber: LOCALSESSIONDATAKEYS.SERIAL_NUMBER,
                printerType: LOCALSESSIONDATAKEYS.PRINTER_TYPE,
            };
            updateDeviceInfoToLocalStorage(selectedDeviceInfo, deviceKeysToSessionMap);

            // call GetUserDeviceRegistration API for selected device to fetch the widgests data
            // accordingly.i.e., either specific to EcoPro or BrotherPlus or Both
            dispatch(GetUserDeviceRegistrationServiceInit());
        }
    }, [selectedDeviceInfo, dispatch]);

    useEffect(() => {
        if (displayEnrollmentInProgressMessage) {

            // fetch refreshed data by calling devices related APIs again
            const timer = setTimeout(() => {
                // reset both API to avoid duplicacy, because both API results are being merged later in code
                dispatch(UserEnrolledDevicesServiceResetState());
                dispatch(UserEnrollmentFailedDevicesServiceReset());

                // intiate APi calls
                dispatch(UserEnrolledDevicesServiceInit());
                dispatch(UserEnrollmentFailedDevicesServiceInit());
                removeNativeAppStorageItems();

                // remove the value form redux storage
                dispatch(ResetEnrollmentJourneySerialNumber());

                // stop displaying enrollment in progress message
                setDisplayEnrollmentInProgressMessage(false);
            }, 30000);

            // clean up: clear timer
            return () => { clearTimeout(timer) };
        }
    }, [displayEnrollmentInProgressMessage, dispatch]);


    useEffect(() => {
        if (
            enrollmentJourneySerialNumber &&
            userDeviceList_success &&
            userEnrollmentFailedDevicesResponse.success
        ) {
            const exists = deviceData?.some(device => {
                return device.serialNumber?.toLowerCase() ===
                    enrollmentJourneySerialNumber?.toLowerCase();
            });
            if (!exists) {
                setDisplayEnrollmentInProgressMessage(true);
            } else {
                setDisplayEnrollmentInProgressMessage(false);

            }
        }
    }, [
        deviceData,
        userDeviceList_success,
        userEnrollmentFailedDevicesResponse.success,
        enrollmentJourneySerialNumber,
        dispatch
    ]);

    useEffect(() => {
        if (isEasyBuyDeepLink && deepLinkSource) {
            pushMultipleDataLayer('easybuy_deeplink_landed', {
                source: deepLinkSource,
                deviceId: deepLinkDeviceId
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEasyBuyDeepLink, deepLinkSource]);

    const handleDeviceClick = useCallback((deviceInfo) => {
        setSelectedDeviceInfo(deviceInfo);

        // upon clicking on device, display details only for successfully enrolled devices
        if (deviceInfo.isEnrollmentSuccessful) {
            if (deviceInfoPanel.current) {
                deviceInfoPanel.current.scrollIntoView({ behavior: "smooth" });
            }
            dispatch(EnrollmentStatus(true));
        }
    }, [dispatch]);

    if (isInvalidDeepLinkSource) {
        return <ErrorPage errorInfo={t("ERROR_CONTENT.ERR_INFO.UNAUTHORIZED_ACCESS")}></ErrorPage>
    }

    if (userDeviceRegistrationResponse.error) {
        return <ErrorPage errorInfo={userDeviceRegistrationResponse.error}></ErrorPage>
    }
    return (
        <div className="my-devices-container">
            <div className="my-devices-content-wrapper">
                <HeaderComponent title={t('MY_DEVICES.HEADER.TITLE')}></HeaderComponent>
                <div className="add-devices-container">
                    <ChooseDeviceBelow />
                </div>

                {
                    invalidDeepLinkDevice && (
                        <EcoproAlert
                            alertImg={WarningAlert}
                            alertHeader={t('DEEP_LINK.INVALID_DEVICE_HEADER')}
                            alertSubHeader={t('DEEP_LINK.INVALID_DEVICE_MESSAGE')}
                            alertClass="dashboard-warning"
                            buttonInAlert={false}
                            alertBtnText={null}
                            showAlertDismiss={true}
                        />
                    )
                }

                {
                    (!userDeviceList_loading && displayEnrollmentInProgressMessage)
                        ? <EcoproAlert
                            alertImg={WarningAlert}
                            alertHeader={t('ECOPRO_ENROLLMENT_ERROR_MESSAGE.ENROLLMENT_PROGRESS')}
                            isSubheaderButton={true}
                            alertSubHeader={t('ECOPRO_ENROLLMENT_WARNING_MESSAGE.ENROLLMENT_INPROGRESS', { 'enrollmentJourneySerialNumber': enrollmentJourneySerialNumber })}
                            alertClass="dashboard-warning"
                            buttonInAlert={false}
                            alertBtnText={null}
                            showAlertDismiss={false}
                        />
                        : null
                }

                {
                    (userDeviceList_error || (userDeviceList_success && deviceData.length === 0))
                        ? <p className="info-error-message">{t('ERROR_CONTENT.NO_DEVICES')}</p>
                        : <>
                            <DeviceListSliderView
                                isLoading={userDeviceList_loading}
                                deviceData={deviceData}
                                selectedDeviceInfo={selectedDeviceInfo}
                                onSelectDevice={handleDeviceClick}
                                shouldBringSelectedDeviceIntoView={
                                    isEasyBuyDeepLink && !invalidDeepLinkDevice
                                }
                            />
                            <DeviceListStackedView
                                isLoading={userDeviceList_loading}
                                deviceData={deviceData}
                                selectedDeviceInfo={selectedDeviceInfo}
                                onSelectDevice={handleDeviceClick}
                            />
                        </>
                }

                {(!userDeviceList_loading && deviceData?.filter(device => device.isEnrollmentSuccessful).length < 5
                    && IsBrotherPlusFeatureSwitchEnabled)
                    ? <AddDevice />
                    : null
                }

                {(!userDeviceList_loading && deviceData?.length > 0)
                    ? <div className="printer-device-panel-wrapper" ref={deviceInfoPanel}>

                        {
                            // Show alert ONLY if EcoPro failed
                            isEcoProFailed && (
                                <div className="ecopro-failed-alert-wrapper">
                                    <EcoproAlert
                                        alertImg={EnrollmentFailedDeviceIcon}
                                        alertHeader={t('ECOPRO_ENROLLMENT_ERROR_MESSAGE.ENROLLMENT_FAILED_MESSAGE')}
                                        isSubheaderButton={true}
                                        alertSubHeader={
                                            getEnrollmentFailureErrorMessage(
                                                selectedDeviceInfo?.services?.ecopro?.status
                                            ) ?? t('ECOPRO_ENROLLMENT_ERROR_MESSAGE.COMMON_ERROR_MESSAGE')
                                        }
                                        alertClass="dashboard-error"
                                        buttonInAlert={false}
                                        alertBtnText={null}
                                        showAlertDismiss={false}
                                    /></div>
                            )
                        }

                        {
                            // Show panel if ANY service is usable
                            isDeviceUsable && selectedDeviceInfo && Object.keys(selectedDeviceInfo).length > 0 && (
                                <PrinterDevicePanel
                                    deviceData={selectedDeviceInfo}
                                    openEasyBuy={isEasyBuyDeepLink && !invalidDeepLinkDevice}
                                    scrollOnMount={isEasyBuyDeepLink && !invalidDeepLinkDevice && !hasScrolledToEasyBuy.current}
                                    onEasyBuyScrolled={handleEasyBuyScrolled}
                                />)
                        }

                    </div>
                    : null
                }

                <SectionHeader imageSrc={sparkleWandIcon} title={t('MY_DEVICES.HEADER.EXPLORE_MORE')} />

                <div className="explore-more-container">
                    {countryCode !== "IT" && <RecycleComponent />}
                    <InspireComponent />
                    <ExploreComponent />
                    <NeedHelpComponent />
                </div>


            </div>
        </div>
    )
}

export default MyDevices;